const HISTORY_REQUEST_TIMEOUT_MS = 15_000;

type HistoryErrorKind = 'offline' | 'timeout' | 'server' | 'client' | 'network' | 'unknown';
type HistoryErrorAction = 'load' | 'loadMore' | 'rename' | 'delete';
type HistoryErrorMessageKey =
  | 'components.aiChatDrawer.history.loadError'
  | 'components.aiChatDrawer.history.loadErrorOffline'
  | 'components.aiChatDrawer.history.loadMoreError'
  | 'components.aiChatDrawer.history.loadMoreErrorOffline'
  | 'components.aiChatDrawer.history.renameError'
  | 'components.aiChatDrawer.history.renameErrorOffline'
  | 'components.aiChatDrawer.history.deleteError'
  | 'components.aiChatDrawer.history.deleteErrorOffline';

const HISTORY_ERROR_MESSAGE_KEYS = {
  load: {
    generic: 'components.aiChatDrawer.history.loadError',
    offline: 'components.aiChatDrawer.history.loadErrorOffline',
  },
  loadMore: {
    generic: 'components.aiChatDrawer.history.loadMoreError',
    offline: 'components.aiChatDrawer.history.loadMoreErrorOffline',
  },
  rename: {
    generic: 'components.aiChatDrawer.history.renameError',
    offline: 'components.aiChatDrawer.history.renameErrorOffline',
  },
  delete: {
    generic: 'components.aiChatDrawer.history.deleteError',
    offline: 'components.aiChatDrawer.history.deleteErrorOffline',
  },
} satisfies Record<HistoryErrorAction, { generic: HistoryErrorMessageKey; offline: HistoryErrorMessageKey }>;

class HistoryError extends Error {
  readonly status: number;

  readonly kind: HistoryErrorKind;

  constructor(status: number, message: string, kind: HistoryErrorKind = 'unknown') {
    super(message);
    this.name = 'HistoryError';
    this.status = status;
    this.kind = kind;
  }
}

function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function classifyStatus(status: number): HistoryErrorKind {
  if (status >= 500) {
    return 'server';
  }
  if (status >= 400) {
    return 'client';
  }
  return 'unknown';
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function toHistoryError(error: unknown): HistoryError {
  if (error instanceof HistoryError) {
    return error;
  }

  if (isBrowserOffline()) {
    return new HistoryError(0, 'You appear to be offline', 'offline');
  }

  if (isAbortError(error)) {
    return new HistoryError(0, 'Request timed out', 'timeout');
  }

  if (error instanceof TypeError) {
    return new HistoryError(0, error.message, 'network');
  }

  const message = error instanceof Error ? error.message : 'Unknown error';
  return new HistoryError(0, message, 'unknown');
}

function isHistoryOfflineError(error: unknown): boolean {
  if (error instanceof HistoryError) {
    return error.kind === 'offline';
  }
  return isBrowserOffline();
}

function getHistoryErrorMessageKey(action: HistoryErrorAction, error: unknown): HistoryErrorMessageKey {
  const variant = isHistoryOfflineError(error) ? 'offline' : 'generic';
  return HISTORY_ERROR_MESSAGE_KEYS[action][variant];
}

async function runWithRequestTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number = HISTORY_REQUEST_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await run(controller.signal);
  } catch (error) {
    if (timedOut) {
      throw new HistoryError(0, 'Request timed out', isBrowserOffline() ? 'offline' : 'timeout');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export type { HistoryErrorKind };
export {
  HistoryError,
  HISTORY_REQUEST_TIMEOUT_MS,
  getHistoryErrorMessageKey,
  isBrowserOffline,
  isHistoryOfflineError,
  classifyStatus,
  toHistoryError,
  runWithRequestTimeout,
};
